import React, { useState, useRef } from "react";
import SignatureCanvas from 'react-signature-canvas';

const FieldPreview = ({ field, updateField, removeField }) => {
    const sigPadRef = useRef();
    const [isCollapsed, setIsCollapsed] = useState(false);

    // testing 
    const [showConditionalFields, setShowConditionalFields] = useState(false);

    const handleConditionalLogicChange = (option) => {  // Remove field parameter
        const updatedConditions = {
            ...field.conditions,
            [option]: {
                enabled: !(field.conditions?.[option]?.enabled || false),
                fields: field.conditions?.[option]?.fields || []
            }
        };
        
        updateField(field.id, {
            ...field,
            conditions: updatedConditions
        });
    };

    // new addition
    const handleAddConditionalField = (option, fieldType) => {
        const newField = {
            id: Math.random().toString(36).substr(2, 9),
            type: fieldType,
            label: `${option} - ${fieldType}`,
            required: false
        };

        const updatedConditions = {
            ...field.conditions,
            [option]: {
                enabled: true,
                fields: [...(field.conditions?.[option]?.fields || []), newField]
            }
        };

        updateField(field.id, {
            ...field,
            conditions: updatedConditions
        });
    };

    // Handles changes in field values
    const handleFieldChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (!updateField || typeof updateField !== "function") {
            console.error("updateField is not a valid function");
            return;
        }

        updateField(field.id, {
            ...field,
            [name]: type === "checkbox" ? checked : value,
        });
    };

    // Handles changes in options for fields like dropdown, radio buttons, checkboxes
    const handleOptionsChange = (e) => {
        const options = e.target.value.split("\n").map((option) => option.trim());

        if (!updateField || typeof updateField !== "function") {
            console.error("updateField is not a valid function");
            return;
        }

        updateField(field.id, { ...field, options });
    };

    // Handles removing the field
    const handleRemoveField = () => {
        if (!removeField || typeof removeField !== "function") {
            console.error("removeField is not a valid function");
            return;
        }

        removeField(field.id);
    };

    // In renderFieldContent, add a required field toggle before each field:
    const renderFieldContent = () => {
        return (
            <div>
                {/* Required Field Toggle (hide for sections) */}
                {field.type !== "section" && field.type !== "form-header" && field.type !== "image" && (
                    <div className="flex items-center justify-end mb-2">
                        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <input
                                type="checkbox"
                                checked={field.required || false}
                                onChange={(e) => handleFieldChange({
                                    target: {
                                        name: 'required',
                                        type: 'checkbox',
                                        checked: e.target.checked
                                    }
                                })}
                                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            />
                            Required field
                        </label>
                    </div>
                )}

                {(() => {
                    switch (field.type) {
                        case "form-header":
                            return (
                                <div className="p-4 bg-blue-50 dark:bg-gray-800 rounded-lg mb-2">
                                    <input
                                        type="text"
                                        value={field.label}
                                        onChange={e => updateField(field.id, { ...field, label: e.target.value })}
                                        className="w-full text-2xl font-bold bg-transparent border-b-2 border-blue-200 dark:border-blue-700 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 mb-2"
                                        placeholder="Form Title"
                                    />
                                    {/* Divider toggle */}
                                    <label className="flex items-center gap-2 my-2">
                                        <input
                                            type="checkbox"
                                            checked={field.showDivider || false}
                                            onChange={e => updateField(field.id, { ...field, showDivider: e.target.checked })}
                                        />
                                        Show divider between title and description
                                    </label>
                                    <textarea
                                        value={field.description}
                                        onChange={e => updateField(field.id, { ...field, description: e.target.value })}
                                        className="w-full text-base bg-transparent border-b border-gray-200 dark:border-gray-700 focus:outline-none focus:border-blue-400 dark:focus:border-blue-500 resize-none"
                                        placeholder="Form Description"
                                        rows={2}
                                    />
                                </div>
                            );
                        case "image":
                            return (
                                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mb-2 flex flex-col items-center">
                                    <input
                                        type="text"
                                        value={field.image_url || ""}
                                        onChange={e => updateField(field.id, { ...field, image_url: e.target.value })}
                                        className="w-full border px-2 py-1 rounded mb-2"
                                        placeholder="Paste image URL here"
                                    />
                                    <input
                                        type="text"
                                        value={field.alt_text || ""}
                                        onChange={e => updateField(field.id, { ...field, alt_text: e.target.value })}
                                        className="w-full border px-2 py-1 rounded mb-2"
                                        placeholder="Alt text (optional)"
                                    />
                                    {field.image_url && (
                                        <img
                                            src={field.image_url}
                                            alt={field.alt_text || "Form image"}
                                            className="w-full object-cover max-h-48 rounded shadow border"
                                        />
                                    )}
                                </div>
                            );
                        case "short-answer":
                            return <input type="text" className="w-full border px-2 py-3 rounded-2xl dark:bg-black dark:text-white dark:border-gray-700" disabled />;
                        case "number":
                            return <input type="number" min={0} className="w-full border px-2 py-3 rounded-2xl dark:bg-black dark:text-white dark:border-gray-700" disabled />;
                        case "mobile":
                            return <input type="tel" pattern="[0-9]{10}" className="w-full border px-2 py-3 rounded-2xl dark:bg-black dark:text-white dark:border-gray-700" placeholder="Enter mobile number" disabled />;
                        case "phone":
                            return <input type="tel" className="w-full border px-2 py-3 rounded-2xl dark:bg-black dark:text-white dark:border-gray-700" placeholder="Enter phone number" disabled />;
                        case "email":
                            return <input type="email" className="w-full border px-2 py-3 rounded-2xl dark:bg-black dark:text-white dark:border-gray-700" placeholder="Enter email address" disabled />;
                        case "long-answer":
                            return <textarea className="w-full border px-2 py-3 rounded-2xl dark:bg-black dark:text-white dark:border-gray-700" disabled />;
                        case "radio":
                        case "checkboxes":
                            return renderOptionsField(field.type === "radio" ? "radio" : "checkbox");
                        case "dropdown":
                            return renderDropdownField();
                        case "date":
                            return <input type="date" className="w-full border px-2 py-1 rounded dark:bg-black dark:text-white dark:border-gray-700" disabled />;
                        case "date-time":
                            return (
                                <input type="datetime-local" className="w-full border px-2 py-1 rounded dark:bg-black dark:text-white dark:border-gray-700" disabled />
                            );
                        case "file-upload":
                            return <input type="file" className="w-full border px-2 py-1 rounded dark:bg-black dark:text-white dark:border-gray-700" disabled />;
                        case "star-rating":
                            return renderRatingField();
                        case "linear-scale":
                            return renderLinearScaleField();
                        case "section":
                            return renderSectionField();
                        case "secret_token":
                            return (
                                <div>
                                    <input
                                        type="text"
                                        name="default_value"
                                        value={field.default_value || ""}
                                        onChange={e => updateField(field.id, { ...field, default_value: e.target.value })}
                                        className="w-full border px-2 py-3 rounded-2xl dark:bg-black dark:text-white dark:border-gray-700"
                                        placeholder="Enter secret token value"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">This value will be hidden from users.</p>
                                </div>
                            );
                        case "user_password":
                            return (
                                <input
                                    type="password"
                                    name={field.field_name}
                                    placeholder="Enter password"
                                    className="w-full border px-2 py-3 rounded-2xl dark:bg-black dark:text-white dark:border-gray-700"
                                />
                            );                       
                        case "enable_notification":
                            return (
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        name={field.field_name}
                                        className="toggle-checkbox"
                                        disabled
                                    />
                                    <span className="text-sm text-gray-700 dark:text-white">Enable Notification</span>
                                </label>
                            );
                        case "e_signature":

                            const clearSignature = () => {
                                sigPadRef.current.clear();
                                updateField(field.id, { ...field, signatureData: "" });
                            };

                            const saveSignature = () => {
                                const dataUrl = sigPadRef.current.toDataURL();
                                updateField(field.id, { ...field, signatureData: dataUrl });
                            };

                            return (
                                <div className="w-full space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-white">Signature</label>
                                    <SignatureCanvas
                                        penColor="black"
                                        canvasProps={{ width: 500, height: 200, className: 'border rounded-lg bg-white' }}
                                        ref={sigPadRef}
                                        onEnd={saveSignature}
                                    />
                                    <div className="flex gap-2 mt-2">
                                        <button onClick={clearSignature} type="button" className="text-sm text-red-500">Clear</button>
                                    </div>
                                    {field.signatureData && (
                                        <img src={field.signatureData} alt="Signature preview" className="mt-2 border rounded shadow" />
                                    )}
                                </div>
                            );
                        case "grid":
                            return (
                                <div className="w-full space-y-2 text-sm">
                                    <label className="block font-medium mb-1">Questions</label>
                                    {(field.questions || []).map((q, idx) => (
                                        <div key={idx} className="flex items-center gap-2 mb-2">
                                            <input
                                                type="text"
                                                value={q}
                                                onChange={e => {
                                                    const updatedQuestions = [...field.questions];
                                                    updatedQuestions[idx] = e.target.value;
                                                    updateField(field.id, { ...field, questions: updatedQuestions });
                                                }}
                                                className="flex-1 border px-2 py-1 rounded"
                                                placeholder={`Question ${idx + 1}`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const updatedQuestions = field.questions.filter((_, i) => i !== idx);
                                                    updateField(field.id, { ...field, questions: updatedQuestions });
                                                }}
                                                className="text-red-500 hover:text-red-700"
                                                title="Remove question"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => updateField(field.id, { ...field, questions: [...(field.questions || []), ""] })}
                                        className="px-3 py-1 text-white bg-blue-500 rounded text-sm"
                                    >
                                        Add Question
                                    </button>

                                    {/* Column labels */}
                                    <label className="block font-medium mt-4 mb-1">Column Labels</label>
                                    {(field.columns || []).map((col, idx) => (
                                        <div key={idx} className="flex items-center gap-2 mb-2">
                                            <input
                                                type="text"
                                                value={col}
                                                onChange={e => {
                                                    const updatedCols = [...field.columns];
                                                    updatedCols[idx] = e.target.value;
                                                    updateField(field.id, { ...field, columns: updatedCols });
                                                }}
                                                className="flex-1 border px-2 py-1 rounded"
                                                placeholder={`Column ${idx + 1}`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const updatedCols = field.columns.filter((_, i) => i !== idx);
                                                    updateField(field.id, { ...field, columns: updatedCols });
                                                }}
                                                className="text-red-500 hover:text-red-700"
                                                title="Remove column"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => updateField(field.id, { ...field, columns: [...(field.columns || []), ""] })}
                                        className="px-3 py-1 text-white bg-blue-500 rounded text-sm"
                                    >
                                        Add Column
                                    </button>

                                    {/* Preview */}
                                    <div className="mt-4">
                                        <table className="w-full border-collapse">
                                            <thead>
                                                <tr>
                                                    <th className="border p-2">Question</th>
                                                    {(field.columns || []).map((col, idx) => (
                                                        <th key={idx} className="border p-2 text-center">{col}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(field.questions || []).map((q, rowIndex) => (
                                                    <tr key={rowIndex}>
                                                        <td className="border p-2">{q}</td>
                                                        {(field.columns || []).map((col, colIdx) => (
                                                            <td key={colIdx} className="border text-center">
                                                                <input type="radio" disabled />
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        case "repeat_group":
                            const repeatValues = field.values || [];

                            const addRepeat = () => {
                                const updated = [...repeatValues, { name: "", email: "" }];
                                updateField(field.id, { ...field, values: updated });
                            };

                            const updateRepeat = (index, key, value) => {
                                const updated = [...repeatValues];
                                updated[index][key] = value;
                                updateField(field.id, { ...field, values: updated });
                            };

                            const removeRepeat = (index) => {
                                const updated = [...repeatValues];
                                updated.splice(index, 1);
                                updateField(field.id, { ...field, values: updated });
                            };

                            return (
                                <div className="w-full space-y-3">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-white">{field.label || "Repeater"}</label>
                                    {repeatValues.map((item, index) => (
                                        <div key={index} className="flex flex-col md:flex-row gap-2 items-center border p-2 rounded">
                                            <input
                                                type="text"
                                                placeholder="Name"
                                                value={item.name}
                                                onChange={e => updateRepeat(index, 'name', e.target.value)}
                                                className="flex-1 border px-2 py-1 rounded"
                                            />
                                            <input
                                                type="email"
                                                placeholder="Email"
                                                value={item.email}
                                                onChange={e => updateRepeat(index, 'email', e.target.value)}
                                                className="flex-1 border px-2 py-1 rounded"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeRepeat(index)}
                                                className="text-red-500 text-sm"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={addRepeat}
                                        className="px-3 py-1 text-white bg-blue-500 rounded text-sm"
                                    >
                                        Add More
                                    </button>
                                </div>
                            );
                        default:
                            return <p className="text-gray-500 dark:text-white">Unsupported field type</p>;
                        }
                })()}
            </div>
        );
    };

    // Renders options-based fields (checkboxes, radio buttons)
    const renderOptionsField = (inputType) => (
        <div>
            <textarea
                name="options"
                placeholder="Enter each option on a new line"
                value={(field.options || []).join("\n")}
                onChange={handleOptionsChange}
                className="w-full border px-2 py-1 rounded mb-2 dark:bg-black dark:bg-border-gray-700 dark:text-white"
            ></textarea>
            {(field.options || []).map((option, index) => (
                <div key={index} className="flex items-center gap-2 dark:bg-black dark:text-white">
                    <input type={inputType} name={field.id} disabled />
                    <span>{option}</span>
                </div>
            ))}
        </div>
    );

    // Renders dropdown field
    const renderDropdownField = () => (
        <div>
            <textarea
                name="options"
                placeholder="Enter each option on a new line"
                value={(field.options || []).join("\n")}
                onChange={handleOptionsChange}
                className="w-full border px-2 py-1 rounded mb-2"
            ></textarea>
            
            {/* new addition */}
            <div className="mb-4">
                <label className="flex items-center gap-2">
                    <input
                    type="checkbox"
                    checked={showConditionalFields}
                    onChange={() => setShowConditionalFields(!showConditionalFields)}
                    />
                    Enable conditional fields
                </label>
            </div>


            {/* // Modify the conditional fields rendering section */}
            {showConditionalFields && (
                <div className="ml-4 border-l-2 pl-4 mb-4">
                    {(field.options || []).map((option, index) => (
                        <div key={index} className="mb-4">
                            <h4 className="font-medium mb-2">If user selects "{option}":</h4>
                            <label className="flex items-center gap-2 mb-2">
                                <input
                                    type="checkbox"
                                    checked={field.conditions?.[option]?.enabled || false}
                                    onChange={() => handleConditionalLogicChange(option)}  // Simplified call

                                />
                                Show additional fields
                            </label>

                            {/* // In the renderDropdownField function, modify the conditional field rendering: */}
                            {field.conditions?.[option]?.enabled && (
                                <div className="ml-4 mt-2">
                                    {/* Field selector */}
                                    <select 
                                        className="w-full border px-2 py-1 rounded mb-2"
                                        onChange={(e) => handleAddConditionalField(option, e.target.value)}
                                        value=""
                                    >
                                        <option value="" disabled>Add a field</option>
                                        <option value="short-answer">Short Answer</option>
                                        <option value="long-answer">Long Answer</option>
                                        <option value="number">Number</option>
                                        <option value="date">Date</option>
                                    </select>

                                    {/* Show existing conditional fields */}
                                    {field.conditions[option].fields.map((conditionalField, idx) => (
                                        <div key={idx} className="mb-2 p-2 border rounded">
                                            <div className="flex flex-col gap-2">
                                                <div className="flex justify-between items-center">
                                                    <input
                                                        type="text"
                                                        value={conditionalField.label}
                                                        onChange={(e) => {
                                                            const updatedFields = [...field.conditions[option].fields];
                                                            updatedFields[idx] = {
                                                                ...conditionalField,
                                                                label: e.target.value
                                                            };
                                                            updateField(field.id, {
                                                                ...field,
                                                                conditions: {
                                                                    ...field.conditions,
                                                                    [option]: {
                                                                        ...field.conditions[option],
                                                                        fields: updatedFields
                                                                    }
                                                                }
                                                            });
                                                        }}
                                                        className="border px-2 py-1 rounded flex-1"
                                                    />
                                                    <button 
                                                        onClick={() => {
                                                            const updatedFields = field.conditions[option].fields.filter((_, i) => i !== idx);
                                                            updateField(field.id, {
                                                                ...field,
                                                                conditions: {
                                                                    ...field.conditions,
                                                                    [option]: {
                                                                        ...field.conditions[option],
                                                                        fields: updatedFields
                                                                    }
                                                                }
                                                            });
                                                        }}
                                                        className="text-red-500 hover:text-red-700 ml-2"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                                {/* Required field toggle for conditional fields */}
                                                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                    <input
                                                        type="checkbox"
                                                        checked={conditionalField.required || false}
                                                        onChange={(e) => {
                                                            const updatedFields = [...field.conditions[option].fields];
                                                            updatedFields[idx] = {
                                                                ...conditionalField,
                                                                required: e.target.checked
                                                            };
                                                            updateField(field.id, {
                                                                ...field,
                                                                conditions: {
                                                                    ...field.conditions,
                                                                    [option]: {
                                                                        ...field.conditions[option],
                                                                        fields: updatedFields
                                                                    }
                                                                }
                                                            });
                                                        }}
                                                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                    />
                                                    Required field
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* new addition (similar to the old) */}
            <select className="w-full border px-2 py-1 rounded" disabled>
                {(field.options || []).map((option, index) => (
                    <option key={index}>{option}</option>
                ))}
            </select>

        </div>
    );

    // Renders star-rating field
    const renderRatingField = () => (
        <div className="flex gap-2">
            {[...Array(field.maxRating || 5)].map((_, index) => (
                <span key={index} className="text-gray-500">
                    ★
                </span>
            ))}
        </div>
    );

    // Renders linear scale field
    const renderLinearScaleField = () => (
        <div className="flex items-center gap-2">
            <input
                type="number"
                name="min"
                value={field.min || 1}
                onChange={handleFieldChange}
                className="w-20 border px-2 py-1 rounded"
                placeholder="Min"
            />
            <span>to</span>
            <input
                type="number"
                name="max"
                value={field.max || 5}
                onChange={handleFieldChange}
                className="w-20 border px-2 py-1 rounded"
                placeholder="Max"
            />
        </div>
    );

    // Renders section field
    const renderSectionField = () => (
        <div className="p-4 rounded-lg space-y-4 shadow-md">
            <div>
                <label className="block text-lg font-semibold text-gray-700 dark:text-white">Section Title</label>
                <input
                    type="text"
                    name="sectionTitle"
                    value={field.sectionTitle || ""}
                    onChange={handleFieldChange}
                    className="w-full text-lg font-medium border dark:bg-black dark:text-white dark:border-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter the section title"
                />
            </div>
            <div>
                <label className="block text-lg font-semibold text-gray-700 dark:text-white">Section Description</label>
                <textarea
                    name="sectionDescription"
                    value={field.sectionDescription || ""}
                    onChange={handleFieldChange}
                    className="w-full text-sm text-gray-700 border dark:bg-black dark:text-white dark:border-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Provide a description for this section"
                    rows="4"
                ></textarea>
            </div>
        </div>
    );

    return (
        <div className="p-2 bg-white dark:bg-black dark:border-gray-700 rounded-2xl shadow border border-gray-200">
            <div className="flex justify-between items-center bg-gray-100 dark:bg-gray-900 rounded-2xl dark:text-white py-3 px-6">
                <input
                    type="text"
                    name="label"
                    value={field.label}
                    onChange={handleFieldChange}
                    className="w-full text-lg font-semibold bg-transparent border-none focus:outline-none focus:ring-0 rounded-xl"
                    placeholder="Field Label"
                />
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="text-gray-500 hover:text-gray-800 dark:text-white"
                        title={isCollapsed ? "Expand" : "Collapse"}
                    >
                        {isCollapsed ? "+" : "−"}
                    </button>
                    <button
                        onClick={handleRemoveField}
                        className="text-red-500 hover:text-red-700 font-bold"
                        title="Remove Field"
                    >
                        ✕
                    </button>
                </div>
            </div>

            {!isCollapsed && <div className="p-2">{renderFieldContent()}</div>}
        </div>
    );
};

export default FieldPreview;
